import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { Box, Menu, MenuItem, Tab, Tabs, Typography, useTheme } from '@mui/material'
import React, { ReactNode, startTransition, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

export interface TabConfig {
  /** Tab label text */
  label: string
  /** Route path segment (e.g., 'details', 'deals') */
  path: string
  /** Whether the tab is disabled */
  disabled?: boolean
  /** Custom label component (overrides label string) */
  customLabel?: ReactNode
}

export interface TabNavigationProps {
  /** Array of tab configurations */
  tabs: TabConfig[]
  /** Base path for navigation (e.g., '/deals/PK/SK' or '/organizations/PK') */
  basePath: string
  /** Callback when tab changes (optional) */
  onTabChange?: (index: number, path: string) => void
  /** Whether to use scrollable tabs (default: false) */
  scrollable?: boolean
  /** Custom styles for the Tabs container */
  sx?: object
  /** Tabs to show in a "More" overflow menu */
  overflowTabs?: TabConfig[]
}

const TabNavigation: React.FC<TabNavigationProps> = ({
  tabs,
  basePath,
  onTabChange,
  scrollable = false,
  sx,
  overflowTabs = [],
}) => {
  const theme = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const [tabValue, setTabValue] = useState(0)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const menuOpen = Boolean(menuAnchor)

  // Check if current route matches an overflow tab
  const activeOverflowTab = overflowTabs.find(tab => location.pathname.includes(`/${tab.path}`))

  // The "More" tab index (appended after primary tabs)
  const moreTabIndex = overflowTabs.length > 0 ? tabs.length : -1

  // Sync tab value with current route
  useEffect(() => {
    const currentPath = location.pathname
    const tabIndex = tabs.findIndex(tab => currentPath.includes(`/${tab.path}`))
    if (tabIndex !== -1) {
      setTabValue(tabIndex)
    } else if (activeOverflowTab) {
      // An overflow tab is active — highlight the "More" tab
      setTabValue(moreTabIndex)
    } else {
      setTabValue(0)
    }
  }, [location.pathname, tabs, activeOverflowTab, moreTabIndex])

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    // If clicking the "More" tab, open the menu instead of navigating
    if (newValue === moreTabIndex) return
    setTabValue(newValue)
    if (onTabChange) {
      onTabChange(newValue, tabs[newValue].path)
    }
  }

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleOverflowSelect = (tab: TabConfig) => {
    handleMenuClose()
    // startTransition marks this navigation as non-urgent — React keeps
    // the current tab visible while the next tab's heavy component
    // tree (e.g., ViewAgreement at ~1000 lines / 32 hooks) mounts in
    // the background, then swaps when ready. Eliminates the "empty
    // flash" between tabs.
    startTransition(() => {
      navigate(`${basePath}/${tab.path}`)
    })
  }

  const handleTabSelect = (tab: TabConfig) => {
    if (tab.disabled) return
    startTransition(() => {
      navigate(`${basePath}/${tab.path}`)
    })
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        overflowX: scrollable ? 'auto' : 'visible',
      }}
    >
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant={scrollable ? 'scrollable' : 'standard'}
        scrollButtons={scrollable ? 'auto' : false}
        allowScrollButtonsMobile={scrollable}
        aria-label="navigation tabs"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: theme.palette.background.section,
          width: '100%',
          minHeight: 40,
          '& .MuiTab-root': {
            minHeight: 40,
          },
          ...(scrollable && {
            '& .MuiTabs-scrollButtons': {
              border: `1px solid ${theme.palette.text.primary}`,
              '&.Mui-disabled': {
                opacity: 0.3,
              },
            },
          }),
          ...sx,
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.path}
            label={
              tab.customLabel || (
                <Typography noWrap sx={{ fontSize: 13 }}>{tab.label}</Typography>
              )
            }
            // Was `component={Link}` — react-router's Link performs a
            // synchronous navigation which forces React to unmount the
            // current tab before rendering the next. For heavy tabs
            // (ViewAgreement, Details, Review) that's a visible ~1s
            // stutter. onClick + startTransition below yields the same
            // navigation but React can keep the old tab visible while
            // the new one mounts concurrently.
            onClick={() => handleTabSelect(tab)}
            disabled={tab.disabled}
            sx={{
              pointerEvents: tab.disabled ? 'none' : 'auto',
              cursor: tab.disabled ? 'default' : 'pointer',
            }}
          />
        ))}

        {/* "More" overflow tab */}
        {overflowTabs.length > 0 && (
          <Tab
            key="__more__"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <MoreHorizIcon sx={{ fontSize: 18 }} />
                <Typography noWrap sx={{ fontSize: 13 }}>
                  {activeOverflowTab ? activeOverflowTab.label : 'More'}
                </Typography>
              </Box>
            }
            onClick={handleMoreClick}
            sx={{
              minWidth: 'auto',
              color: activeOverflowTab ? theme.palette.primary.main : undefined,
            }}
          />
        )}
      </Tabs>

      {/* Overflow menu */}
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 160,
              bgcolor: theme.palette.background.section,
              border: `1px solid ${theme.palette.divider}`,
            },
          },
        }}
      >
        {overflowTabs.map(tab => (
          <MenuItem
            key={tab.path}
            onClick={() => handleOverflowSelect(tab)}
            disabled={tab.disabled}
            selected={location.pathname.includes(`/${tab.path}`)}
            sx={{ fontSize: 13 }}
          >
            {tab.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

export default TabNavigation
